import { getAuthToken } from "@app/relay/fetch-relay";
import { useToast } from "@app/components/toast";
import { useState, useRef, useTransition } from "react";
import type { ReactNode } from "react";

// The shared body of every transaction drill-down side panel (P&L cell, revenue
// cell, cash-flow bar): a sortable table, multi-select with shift-range, a header
// with the running total, and an "Export .xls" button that downloads the COMPLETE
// filtered set (not the 500-row preview). Per-panel bulk actions are injected via
// `renderBulkActions` — the only thing that differs between panels — so this stays
// a presentational shell, not a god-component toggled by mode flags.
//
// The host panel keeps its own relay query + Dialog chrome (using
// TXN_PANEL_DIALOG_CLASS for a consistent wide layout) and passes the fetched
// rows down. Sort state lives in the host (it drives the query); see useTxnSort.

// Consistent wide side-panel sizing. A 6-column table needs the room; the old
// list panels were w-[680px]/w-[720px].
export const TXN_PANEL_DIALOG_CLASS =
  "fixed right-0 top-0 h-full w-[1180px] max-w-[97vw] bg-white shadow-2xl z-50 flex flex-col focus:outline-none";

export type SortDirection = "ASC" | "DESC";
export type TxnSortColumn = "DATE" | "AMOUNT" | "DESCRIPTION" | "COUNTERPARTY" | "CATEGORY" | "NATURE" | "ACCOUNT";

// Sort state for a drill-down panel. Lives in the host (it feeds the relay query
// variables, so changing it re-fetches the server-sorted page — the full set,
// not just a client-side reorder of the truncated preview). Clicking the active
// column flips direction; a new column starts DESC for amount/date, ASC for text.
//
// The flip runs inside a transition: new query variables suspend the host's
// query, and outside a transition that drops the WHOLE panel to its Suspense
// fallback for a sort click. Inside one, React keeps the current table on screen
// and reports `sorting`, which the panel renders as the dimmed, aria-busy table.
export function useTxnSort(initialColumn: TxnSortColumn = "DATE", initialDirection: SortDirection = "DESC") {
  const [state, setState] = useState<{ column: TxnSortColumn; direction: SortDirection }>({
    column: initialColumn,
    direction: initialDirection,
  });
  const [sorting, startSorting] = useTransition();
  const onSort = (column: TxnSortColumn) =>
    startSorting(() =>
      setState((prev) =>
        prev.column === column
          ? { column, direction: prev.direction === "ASC" ? "DESC" : "ASC" }
          : { column, direction: column === "AMOUNT" || column === "DATE" ? "DESC" : "ASC" }
      )
    );
  return {
    sort: state.column,
    direction: state.direction,
    onSort,
    sorting,
    // Spread into the relay query variables.
    sortVars: { sort: state.column, sortDirection: state.direction },
  };
}

// The row shape the table renders. Each panel's relay query must select at least
// these fields (tsc enforces it at the call site); extra fields are fine. The
// nullable fields allow `undefined` as well as `null` because relay types
// optional GraphQL fields as `T | null | undefined`.
export interface TxnTableRow {
  readonly txn: { readonly id: string };
  readonly md5: string;
  readonly postingDate: string;
  readonly description: string;
  readonly amount: number; // already dollars (TransactionRowType.amount)
  readonly kind: string;
  readonly accountNumber: string | null | undefined;
  readonly internal: boolean;
  readonly counterparty: { readonly name: string } | null | undefined;
  readonly category: string | null | undefined;
  readonly expenseNature: { readonly id: string; readonly name: string } | null | undefined;
  readonly classification: string | null | undefined;
  // Optional source-provenance signal (U13): select TransactionRow.qboWitnessed
  // to light up the bank / bank+QBO badge states; hosts that don't select it
  // still get the QBO badge from the kind alone.
  readonly qboWitnessed?: boolean | null;
}

const QBO_KINDS = new Set(["QBO_SOURCED", "QBO_GAP", "QBO_ANCHOR"]);

// The source-provenance badge (U13): which arm(s) evidence this transaction.
//   flagged  — host-supplied (the verdict map lives with the panel context):
//              a demoted/needs-review QBO-backed txn.
//   QBO      — a QBO-materialized txn (kind is QBO-namespaced).
//   bank+QBO — a bank txn carrying a QBO witness (matched across sources).
//   bank     — a bank txn with no witness (only when the host selected the
//              qboWitnessed field; otherwise no badge rather than a guess).
export function SourceBadge({
  kind,
  qboWitnessed,
  flagged = false,
}: {
  kind: string;
  qboWitnessed?: boolean | null;
  flagged?: boolean;
}) {
  if (flagged) {
    return <span className="rounded bg-red-50 px-1.5 py-0.5 text-[10px] font-medium text-red-700">flagged</span>;
  }
  if (QBO_KINDS.has(kind)) {
    return <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">QBO</span>;
  }
  if (qboWitnessed === true) {
    return <span className="rounded bg-indigo-50 px-1.5 py-0.5 text-[10px] font-medium text-indigo-700">bank + QBO</span>;
  }
  if (qboWitnessed === false) {
    return <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500">bank</span>;
  }
  return null;
}

// TransactionRowType.amount is already dollars (Float), not cents.
const money2 = (dollars: number) =>
  dollars.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 });

const money0 = (dollars: number) =>
  dollars.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

const amountColor = (row: TxnTableRow) =>
  row.internal ? "text-gray-400" : row.amount < 0 ? "text-red-600" : "text-green-600";

// A host-supplied extra column (e.g. needs-review's From/To) rendered after the
// standard non-amount columns. `render` receives the full (host-typed) row.
export type TxnColumnDef<R extends TxnTableRow = TxnTableRow> = {
  readonly key: string;
  readonly header: ReactNode;
  readonly align?: "left" | "right";
  readonly render: (row: R) => ReactNode;
};

// The three orthogonal axes (the unified model):
//   mode        — chrome: a right drawer (default), inline in a card, or full page.
//   interaction — read-only (no selection), or selectable/editable (checkboxes + bulk bar).
//   actions     — host-injected via renderBulkActions (+ extraColumns / provenance slots).
// All new props are optional and default to the original drawer behavior, so the
// existing drill panels render identically.
type TxnTablePanelProps<R extends TxnTableRow> = {
  title: string;
  totalCount: number;
  rows: readonly R[];
  emptyLabel: string;
  sort: TxnSortColumn;
  direction: SortDirection;
  onSort: (column: TxnSortColumn) => void;
  exportParams: Record<string, string>;
  busy?: boolean;
  // From useTxnSort — the sort refetch in flight; dims the table like `busy`.
  sorting?: boolean;
  onClose?: () => void;
  renderBulkActions?: (selectedIds: string[], clearSelection: () => void) => ReactNode;
  // The authoritative total over the WHOLE filtered set (dollars), for panels whose
  // drill can exceed the 500-row preview — so the header sum reconciles to the clicked
  // number rather than summing only the shown rows. Omit to use the shown-rows sum.
  headerTotalDollars?: number;
  mode?: "drawer" | "inline" | "page";
  interaction?: "read-only" | "selectable" | "editable";
  // Optional offset pagination (page-mode surfaces only — /transactions,
  // /needs-review). When present, `rows` is ONE server-fetched page and the panel
  // renders a Prev/Next bar with "Showing A–B of N" instead of the "first N shown"
  // truncation note. Omit it and the panel behaves exactly as before (every drill
  // drawer relies on that).
  pagination?: {
    page: number; // 0-indexed
    pageSize: number;
    onPageChange: (page: number) => void;
  };
  // Optional provenance renderer for the description cell. Replaces the default
  // SourceBadge — hosts that know more (e.g. the verdict map's flagged state)
  // compose <SourceBadge flagged={...}/> themselves.
  provenance?: (row: R) => ReactNode;
  // Extra columns appended before Amount; standard columns can be hidden.
  extraColumns?: ReadonlyArray<TxnColumnDef<R>>;
  hideColumns?: ReadonlyArray<TxnSortColumn>;
  // Optional per-cell renderers for the three editable dimensions. A host that
  // supplies one owns that cell's whole contents (the wallet ledger renders
  // click-to-edit popovers); omit them and the cells render as read-only text
  // exactly as before, which every other panel relies on.
  renderCounterparty?: (row: R) => ReactNode;
  renderCategory?: (row: R) => ReactNode;
  renderNature?: (row: R) => ReactNode;
};

export default function TxnTablePanel<R extends TxnTableRow = TxnTableRow>({
  title,
  totalCount,
  rows,
  emptyLabel,
  sort,
  direction,
  onSort,
  exportParams,
  busy = false,
  sorting = false,
  onClose,
  renderBulkActions,
  headerTotalDollars,
  mode = "drawer",
  interaction = "selectable",
  provenance,
  extraColumns = [],
  hideColumns = [],
  pagination,
  renderCounterparty,
  renderCategory,
  renderNature,
}: TxnTablePanelProps<R>) {
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const lastIndexRef = useRef<number | null>(null);
  const shiftHeldRef = useRef(false);

  // read-only hides the whole selection apparatus; selectable/editable keep it.
  const selectable = interaction !== "read-only";
  const shown = (column: TxnSortColumn) => !hideColumns.includes(column);
  // When paginating, `rows` is one page by design — the pager conveys the window,
  // so suppress the "first N shown" truncation note.
  const truncated = !pagination && totalCount > rows.length;
  // Sum of the shown rows — equals the clicked number when not truncated, and
  // stays honest as rows leave the corpus after a fix.
  const shownTotal = rows.reduce((sum, row) => sum + row.amount, 0);
  // When the host supplies the full-set total (a drill that can exceed the preview),
  // show it so the header reconciles to the clicked number even when truncated.
  const headerTotal = headerTotalDollars ?? shownTotal;

  const toggleRow = (txnId: string, index: number) => {
    const anchor = lastIndexRef.current;
    lastIndexRef.current = index;
    setSelected((prev) => {
      const next = new Set(prev);
      if (shiftHeldRef.current && anchor != null) {
        const [lo, hi] = anchor < index ? [anchor, index] : [index, anchor];
        for (let i = lo; i <= hi; i++) next.add(rows[i].txn.id);
      } else if (next.has(txnId)) {
        next.delete(txnId);
      } else {
        next.add(txnId);
      }
      return next;
    });
  };

  const allSelected = rows.length > 0 && selected.size === rows.length;
  const clearSelection = () => setSelected(new Set());

  // drawer/page fill their container (flex-1 scroll); inline sizes to content
  // with a capped scroll so it sits naturally inside a card/section.
  const outerClass = mode === "inline" ? "flex flex-col" : "flex flex-col h-full";
  const scrollClass = mode === "inline" ? "overflow-auto max-h-[34rem]" : "overflow-auto flex-1";

  return (
    <div className={outerClass}>
      <div className="flex items-start justify-between px-4 py-3 border-b shrink-0">
        <div>
          <h2 className="font-bold text-gray-800">{title}</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            {totalCount.toLocaleString()} transaction{totalCount === 1 ? "" : "s"} · {money0(headerTotal)}
            {truncated && ` · first ${rows.length} shown`}
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {/* The in-place busy state. It lives in the header, not the selection bar:
              a bulk action clears the selection the moment it succeeds, so an
              indicator inside that bar vanished exactly when the refetch it was
              reporting on began — which is what made a successful edit look like
              nothing had happened. */}
          {busy && (
            <span role="status" aria-live="polite" className="flex items-center gap-1.5 text-xs text-gray-500">
              <span
                aria-hidden
                className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600"
              />
              Updating…
            </span>
          )}
          <ExportButton exportParams={exportParams} disabled={totalCount === 0} />
          {onClose && (
            <button type="button" onClick={onClose} className="text-sm text-gray-500 hover:text-gray-900">
              Close
            </button>
          )}
        </div>
      </div>

      {selectable && selected.size > 0 && (
        <div className="flex flex-wrap items-center gap-3 px-4 py-2 bg-gray-900 text-white text-sm shrink-0">
          <span>{selected.size} selected</span>
          {renderBulkActions?.([...selected], clearSelection)}
          <button type="button" onClick={clearSelection} className="text-xs underline hover:no-underline ml-auto">
            Clear
          </button>
        </div>
      )}

      {pagination && totalCount > pagination.pageSize && (
        <div className="flex items-center justify-between px-4 py-2 text-sm text-gray-500 border-b shrink-0">
          <span>
            Showing {(pagination.page * pagination.pageSize + 1).toLocaleString()}–
            {Math.min((pagination.page + 1) * pagination.pageSize, totalCount).toLocaleString()} of{" "}
            {totalCount.toLocaleString()}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={pagination.page === 0}
              onClick={() => pagination.onPageChange(Math.max(0, pagination.page - 1))}
              className="px-3 py-1 rounded border hover:bg-gray-50 disabled:opacity-30"
            >
              ← Prev
            </button>
            <span className="text-xs">
              Page {pagination.page + 1} of {Math.max(1, Math.ceil(totalCount / pagination.pageSize))}
            </span>
            <button
              type="button"
              disabled={(pagination.page + 1) * pagination.pageSize >= totalCount}
              onClick={() => pagination.onPageChange(pagination.page + 1)}
              className="px-3 py-1 rounded border hover:bg-gray-50 disabled:opacity-30"
            >
              Next →
            </button>
          </div>
        </div>
      )}

      <div className={`${scrollClass} ${busy || sorting ? "opacity-50 transition-opacity" : ""}`} aria-busy={busy || sorting}>
        {rows.length === 0 ? (
          <p className="px-4 py-6 text-sm text-gray-400">{emptyLabel}</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10 bg-gray-50 text-xs text-gray-500 border-b">
              <tr>
                {selectable && (
                  <th className="w-8 px-3 py-2">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={() => setSelected(allSelected ? new Set() : new Set(rows.map((r) => r.txn.id)))}
                      aria-label="Select all"
                    />
                  </th>
                )}
                <SortHeader column="DATE" label="Date" sort={sort} direction={direction} onSort={onSort} />
                {shown("ACCOUNT") && <SortHeader column="ACCOUNT" label="Account" sort={sort} direction={direction} onSort={onSort} />}
                <SortHeader column="DESCRIPTION" label="Description" sort={sort} direction={direction} onSort={onSort} />
                {shown("COUNTERPARTY") && <SortHeader column="COUNTERPARTY" label="Counterparty" sort={sort} direction={direction} onSort={onSort} />}
                {shown("CATEGORY") && <SortHeader column="CATEGORY" label="Category" sort={sort} direction={direction} onSort={onSort} />}
                {shown("NATURE") && <SortHeader column="NATURE" label="Nature" sort={sort} direction={direction} onSort={onSort} />}
                {extraColumns.map((col) => (
                  <th key={col.key} className={`px-3 py-2 font-medium ${col.align === "right" ? "text-right" : "text-left"}`}>
                    {col.header}
                  </th>
                ))}
                <SortHeader column="AMOUNT" label="Amount" sort={sort} direction={direction} onSort={onSort} align="right" />
              </tr>
            </thead>
            <tbody className="divide-y">
              {rows.map((row, index) => (
                <tr key={row.md5} className={selected.has(row.txn.id) ? "bg-amber-50" : "hover:bg-gray-50"}>
                  {selectable && (
                    <td className="px-3 py-2 align-top">
                      <input
                        type="checkbox"
                        checked={selected.has(row.txn.id)}
                        onClick={(event) => {
                          shiftHeldRef.current = event.shiftKey;
                          toggleRow(row.txn.id, index);
                        }}
                        onChange={() => {}}
                        aria-label={`Select row ${index + 1}`}
                      />
                    </td>
                  )}
                  <td className="px-3 py-2 align-top whitespace-nowrap text-gray-500 tabular-nums">
                    {row.postingDate.slice(0, 10)}
                  </td>
                  {shown("ACCOUNT") && (
                    <td className="px-3 py-2 align-top whitespace-nowrap text-gray-500">
                      {row.accountNumber ? `••${row.accountNumber.slice(-4)}` : "—"}
                    </td>
                  )}
                  <td className="px-3 py-2 align-top text-gray-700">
                    <div className="max-w-[28rem] truncate" title={row.description}>
                      {row.description}
                    </div>
                    <div className="text-xs text-gray-400 mt-0.5 flex items-center gap-2 flex-wrap">
                      <span className="uppercase">{row.kind}</span>
                      {row.classification && <span className="text-amber-600">· {row.classification}</span>}
                      {provenance ? (
                        provenance(row)
                      ) : (
                        <SourceBadge kind={row.kind} qboWitnessed={row.qboWitnessed} />
                      )}
                    </div>
                  </td>
                  {shown("COUNTERPARTY") && (
                    <td className="px-3 py-2 align-top text-gray-600">
                      {renderCounterparty ? renderCounterparty(row) : (row.counterparty?.name ?? "—")}
                    </td>
                  )}
                  {shown("CATEGORY") && (
                    <td className="px-3 py-2 align-top text-emerald-700">
                      {renderCategory ? renderCategory(row) : (row.category ?? "—")}
                    </td>
                  )}
                  {shown("NATURE") && (
                    <td className="px-3 py-2 align-top text-indigo-700">
                      {renderNature ? renderNature(row) : (row.expenseNature?.name ?? "—")}
                    </td>
                  )}
                  {extraColumns.map((col) => (
                    <td key={col.key} className={`px-3 py-2 align-top ${col.align === "right" ? "text-right" : ""}`}>
                      {col.render(row)}
                    </td>
                  ))}
                  <td className={`px-3 py-2 align-top text-right whitespace-nowrap tabular-nums font-medium ${amountColor(row)}`}>
                    {money2(row.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function SortHeader({
  column,
  label,
  sort,
  direction,
  onSort,
  align = "left",
}: {
  column: TxnSortColumn;
  label: string;
  sort: TxnSortColumn;
  direction: SortDirection;
  onSort: (column: TxnSortColumn) => void;
  align?: "left" | "right";
}) {
  const active = sort === column;
  return (
    <th className={`px-3 py-2 font-medium ${align === "right" ? "text-right" : "text-left"}`}>
      <button
        type="button"
        onClick={() => onSort(column)}
        className={`inline-flex items-center gap-1 hover:text-gray-900 ${active ? "text-gray-900" : ""} ${
          align === "right" ? "flex-row-reverse" : ""
        }`}
      >
        {label}
        <span className="text-[10px] text-gray-400">{active ? (direction === "ASC" ? "▲" : "▼") : "↕"}</span>
      </button>
    </th>
  );
}

// Downloads the complete filtered set from the authenticated export endpoint.
// The JWT lives in localStorage (not a cookie), so a plain <a href> wouldn't
// carry it — fetch() with the bearer header, then trigger a blob download.
// Mirrors the dashboard board-pack export button.
function ExportButton({ exportParams, disabled }: { exportParams: Record<string, string>; disabled: boolean }) {
  const notify = useToast();
  const [exporting, setExporting] = useState(false);

  const exportXls = async () => {
    setExporting(true);
    try {
      const qs = new URLSearchParams(exportParams).toString();
      const response = await fetch(`/exports/transactions${qs ? `?${qs}` : ""}`, {
        headers: { Authorization: `Bearer ${getAuthToken() ?? ""}` },
      });
      if (response.status === 401) return notify({ message: "Session expired — sign in again.", kind: "error" });
      if (response.status === 403) return notify({ message: "Your data isn't revealed yet.", kind: "error" });
      if (!response.ok) return notify({ message: "Export failed. Please try again.", kind: "error" });

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `transactions-${new Date().toISOString().slice(0, 10)}.xlsx`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch {
      notify({ message: "Export failed. Please try again.", kind: "error" });
    } finally {
      setExporting(false);
    }
  };

  return (
    <button
      type="button"
      disabled={disabled || exporting}
      onClick={exportXls}
      className="rounded border px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900 disabled:opacity-50"
      title="Download every transaction in this view as an Excel (.xlsx) file"
    >
      {exporting ? "Exporting…" : "Export .xls"}
    </button>
  );
}

// A label button that toggles a popover holding its content; closes on pick.
// Shared by the panels' bulk-action toolbars (passed into renderBulkActions).
export function BulkAction({
  label,
  disabled,
  children,
}: {
  label: string;
  disabled: boolean;
  children: (close: () => void) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className="px-2 py-1 rounded bg-white text-gray-900 hover:bg-gray-100 text-xs disabled:opacity-50"
      >
        {label}
      </button>
      {open && (
        <div className="absolute left-0 z-50 mt-1 rounded border bg-white p-2 shadow-lg text-gray-700">
          {children(() => setOpen(false))}
        </div>
      )}
    </div>
  );
}
